import React, { useCallback, useEffect } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * Navigace zpět tam, odkud uživatel přišel — ne na pevně danou stránku.
 *
 * <SledovaniCesty/> (v App) si vede v sessionStorage cestu stránek uvnitř
 * aplikace. Tlačítko „Zpět" pak vrací o krok v historii prohlížeče (zachová
 * filtry a záložku předchozí stránky, protože ty jsou v adrese — useUrlStav)
 * a ukazuje, kam vede. Když předchozí stránka není (otevřený odkaz, obnovení),
 * vede na nadřazenou stránku z drobečků.
 */

const KLIC = "pp_cesta";
const cti = () => { try { return JSON.parse(sessionStorage.getItem(KLIC)) || []; } catch { return []; } };
const zapis = (c) => { try { sessionStorage.setItem(KLIC, JSON.stringify(c.slice(-50))); } catch { /* bez úložiště */ } };

// Výchozí názvy stránek, když stránka svůj název (klient, zařízení…) nenahlásí.
const NAZVY = [
  [/^\/$/, "Domů"], [/^\/klienti\/[^/]+/, "Klient"], [/^\/klienti/, "Klienti"],
  [/^\/obchod\/[^/]+/, "Obchodní případ"], [/^\/obchod/, "Obchodní případy"],
  [/^\/technologie\//, "Technologie"], [/^\/nabidky\/[^/]+/, "Nabídka"], [/^\/nabidky/, "Nabídky"],
  [/^\/partneri\/[^/]+/, "Obchodní partner"], [/^\/partneri/, "Obchodní partneři"],
  [/^\/polozky/, "Ceník"], [/^\/cenik\/zarizeni\//, "Karta zařízení"],
  [/^\/servis\/zarizeni\//, "Zařízení"], [/^\/servis\/zapisy\//, "Servisní zápis"], [/^\/servis/, "Servis"],
  [/^\/doklady\/[^/]+/, "Doklad"], [/^\/doklady/, "Doklady"], [/^\/(kalendar|ukoly)/, "Úkoly"],
  [/^\/ekonomika/, "Ekonomika"], [/^\/kpi/, "KPI"], [/^\/sklad/, "Sklad"],
  [/^\/tym\/[^/]+/, "Člen týmu"], [/^\/tym/, "Tým"], [/^\/nastaveni/, "Nastavení"],
];
// Název nahlášený dřív, než SledovaniCesty stránku zapíše (efekty dětí běží první).
const cekajici = {};
const vychoziNazev = (path) => (NAZVY.find(([rx]) => rx.test(path)) || [null, "Zpět"])[1];

export function SledovaniCesty() {
  const loc = useLocation();
  const typ = useNavigationType();
  useEffect(() => {
    const zaznam = { key: loc.key, path: loc.pathname, search: loc.search, nazev: cekajici[loc.key] || null };
    let c = cti();
    const i = c.findIndex((x) => x.key === loc.key);
    if (loc.key === "default" || !c.length) c = [zaznam];
    else if (i >= 0) c = c.slice(0, i + 1);                               // zpět / vpřed na známou stránku
    else if (typ === "REPLACE") c = [...c.slice(0, -1), { ...zaznam, nazev: zaznam.nazev || (c[c.length - 1]?.path === loc.pathname ? c[c.length - 1].nazev : null) }];
    else c = [...c, zaznam];
    zapis(c);
  }, [loc.key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/** Stránka nahlásí svůj název (jméno klienta, zařízení…) pro tlačítko Zpět jinde. */
export function useNazevStranky(nazev) {
  const loc = useLocation();
  useEffect(() => {
    if (!nazev) return;
    cekajici[loc.key] = nazev;
    const c = cti();
    const z = c.find((x) => x.key === loc.key);
    if (z && z.nazev !== nazev) { z.nazev = nazev; zapis(c); }
  }, [nazev, loc.key]);
}

/** Předchozí stránka v aplikaci (nebo null) a funkce, která se na ni vrátí. */
export function usePredchozi() {
  const loc = useLocation();
  const typ = useNavigationType();
  const nav = useNavigate();
  const c = cti();
  const i = c.findIndex((x) => x.key === loc.key);
  // Stránka, kterou SledovaniCesty ještě nezapsal (první render po přechodu).
  const pred = i > 0 ? c[i - 1] : i === 0 || loc.key === "default" ? null
    : typ === "PUSH" ? c[c.length - 1] || null : typ === "REPLACE" ? c[c.length - 2] || null : null;
  const zpet = useCallback(() => nav(-1), [nav]);
  return { pred: pred && { ...pred, nazev: pred.nazev || vychoziNazev(pred.path) }, zpet };
}

/** Odkaz na stránku: když je to právě předchozí stránka, vrátí se v historii
    (zachová filtry, záložku a místo na stránce), jinak normálně otevře. */
export function useOtevrit() {
  const nav = useNavigate();
  const { pred, zpet } = usePredchozi();
  return useCallback((to) => (pred && pred.path === to ? zpet() : nav(to)), [pred, zpet, nav]);
}

/**
 * Tlačítko Zpět: na předchozí stránku v aplikaci, když existuje; jinak na
 * `nahradni` (nadřazená stránka, např. případ u nabídky).
 */
export function Zpet({ nahradni, nahradniNazev, className = "", testid }) {
  const nav = useNavigate();
  const { pred, zpet } = usePredchozi();
  const nazev = pred ? pred.nazev : nahradniNazev;
  if (!pred && !nahradni) return null;
  return (
    <button onClick={() => (pred ? zpet() : nav(nahradni))} data-testid={testid || "zpet"}
      className={`tap md:min-h-0 inline-flex items-center gap-1.5 t-meta text-muted hover:text-ink max-w-full ${className}`}>
      <ArrowLeft size={15} className="shrink-0" /> <span className="truncate">{nazev}</span>
    </button>
  );
}

/**
 * Drobečky (Klienti / Jan Novák / Zařízení). Když uživatel přišel odjinud
 * (třeba z ceníku), je před nimi navíc „← odkud přišel".
 * `cesta` = [{ nazev, to }], `aktualni` = název této stránky.
 */
export function Drobecky({ cesta = [], aktualni, className = "", testid }) {
  const { pred, zpet } = usePredchozi();
  const otevrit = useOtevrit();
  const z_cesty = pred && cesta.some((x) => x.to === pred.path);
  return (
    <div className={`t-meta text-muted flex flex-wrap items-center gap-y-1 ${className}`} data-testid={testid}>
      {pred && !z_cesty && (
        <>
          <button onClick={zpet} className="tap md:min-h-0 inline-flex items-center gap-1 hover:text-ink font-600 text-sec max-w-full" data-testid="drobecky-zpet">
            <ArrowLeft size={14} className="shrink-0" /> <span className="truncate">{pred.nazev}</span>
          </button>
          <span className="mx-2 text-line-2">|</span>
        </>
      )}
      {cesta.map((x) => (
        <React.Fragment key={x.to}>
          <button onClick={() => otevrit(x.to)} className="tap md:min-h-0 hover:text-ink" data-testid={x.testid}>{x.nazev}</button>
          <span className="mx-1.5 text-line-2">/</span>
        </React.Fragment>
      ))}
      <span className="font-600 text-sec truncate">{aktualni}</span>
    </div>
  );
}

/**
 * Stav uložený v adrese (?tab=klienti, ?vyrobce=…): po návratu zpět je
 * stránka tak, jak ji uživatel opustil. Výchozí hodnota se do adresy nepíše.
 */
export function useUrlStav(klic, vychozi = null) {
  const loc = useLocation();
  const nav = useNavigate();
  const hodnota = new URLSearchParams(loc.search).get(klic);
  const nastav = useCallback((v) => {
    // Čte aktuální adresu (ne tu z renderu) — dvě změny za sebou se nepřepíšou.
    const p = new URLSearchParams(window.location.search);
    const nova = typeof v === "function" ? v(p.get(klic) ?? vychozi) : v;
    if (nova === null || nova === undefined || nova === "" || nova === vychozi) p.delete(klic); else p.set(klic, String(nova));
    const s = p.toString();
    nav({ pathname: window.location.pathname, search: s ? `?${s}` : "" }, { replace: true });
  }, [klic, vychozi, nav]);
  return [hodnota ?? vychozi, nastav];
}
